'use strict';

import * as moment from 'moment';
import countdownTpl from 'templates/__countdown.tpl';
import { getLocale } from 'services/LocalizationService';

/**
 * Countdown component class
 */
export default class Countdown {
    constructor(countdownElement) {
        if (!countdownElement) return;
        this.countdownElement = countdownElement;
        this.currentTimestamp = Date.now();
        this.firstAvailableTimestamp = parseInt(
            this.countdownElement.getAttribute('data-first-available-timestamp'),
        );
        this.startTimestamp = parseInt(this.countdownElement.getAttribute('data-start-timestamp'));
        this.streamStartTimestamp = parseInt(
            this.countdownElement.getAttribute('data-stream-start-timestamp'),
        );
        this.streamEndTimestamp = parseInt(
            this.countdownElement.getAttribute('data-stream-end-timestamp'),
        );

        if (this.firstAvailableTimestamp > 0) {
            this.diffTime = this.firstAvailableTimestamp - this.currentTimestamp;
        } else {
            this.diffTime = this.startTimestamp - this.currentTimestamp;
        }

        this.duration = moment.duration(this.diffTime, 'milliseconds');
        this.years = this.duration.years();
        this.months = this.duration.months();
        this.days = this.duration.days();
        this.hours = this.duration.hours();
        this.mins = this.duration.minutes();
        this.secs = this.duration.seconds();

        if (this.firstAvailableTimestamp > 0) {
            this.totalDays = moment(this.firstAvailableTimestamp).diff(moment(), 'days');
        } else {
            this.totalDays = moment(this.startTimestamp).diff(moment(), 'days');
        }

        this.totalHours = 24;
        this.totalMins = 60;
        this.totalSecs = 60;
        this.interval = 1000;

        this.init();
    }

    init() {
        let self = this;

        self.setup();

        if (
            self.streamEndTimestamp < self.currentTimestamp ||
            (self.firstAvailableTimestamp > 0 &&
                self.firstAvailableTimestamp < self.currentTimestamp)
        ) {
            // stream already over
            self.switchToDone();
        } else if (self.diffTime <= 0) {
            // already live
            self.switchToLive();
        } else {
            // not live yet, start countdown
            self.countInterval = setInterval(function () {
                self.start();
            }, self.interval);
        }

        // show countdown after animation has started
        setTimeout(function () {
            self.countdownElement.querySelector('.countdown').classList.add('show');
        }, self.interval);

        // pre-broadcast stream has started but not live yet
        if (
            self.streamStartTimestamp < self.currentTimestamp &&
            self.currentTimestamp < self.startTimestamp
        ) {
            self.switchToUpcoming();
        }
    }

    // add template markup to countdown element and populate initial values
    setup() {
        this.countdownElement.innerHTML = countdownTpl({ getLocale: getLocale });

        this.populateInitialValues(
            this.countdownElement.querySelectorAll('.countdown__wrap--days')[0],
            this.totalDays,
        );
        this.populateInitialValues(
            this.countdownElement.querySelectorAll('.countdown__wrap--hours')[0],
            this.totalHours,
        );
        this.populateInitialValues(
            this.countdownElement.querySelectorAll('.countdown__wrap--minutes')[0],
            this.totalMins,
        );
        this.populateInitialValues(
            this.countdownElement.querySelectorAll('.countdown__wrap--seconds')[0],
            this.totalSecs,
        );
    }

    // start countdown
    start() {
        this.countdownElement.querySelectorAll('.countdown__wrap span').forEach(function (el) {
            el.classList.remove('active');
        });

        // if countdown is over, stop interval and show live now label
        if (this.duration._milliseconds <= 0) {
            clearInterval(this.countInterval);
            if (!this.firstAvailableTimestamp) {
                this.switchToLive();
            } else {
                this.switchToDone();
            }
            return;
        }

        this.duration = moment.duration(this.duration - this.interval, 'milliseconds');

        let startTimestamps = 0;
        if (this.firstAvailableTimestamp > 0) {
            startTimestamps = this.firstAvailableTimestamp;
        } else {
            startTimestamps = this.startTimestamp;
        }

        this.animateValue(
            this.countdownElement.querySelectorAll('.countdown__wrap--days')[0],
            moment(startTimestamps).diff(moment(), 'days'),
            this.totalDays,
        );
        this.animateValue(
            this.countdownElement.querySelectorAll('.countdown__wrap--hours')[0],
            this.duration.hours(),
            this.totalHours,
        );
        this.animateValue(
            this.countdownElement.querySelectorAll('.countdown__wrap--minutes')[0],
            this.duration.minutes(),
            this.totalMins,
        );
        this.animateValue(
            this.countdownElement.querySelectorAll('.countdown__wrap--seconds')[0],
            this.duration.seconds(),
            this.totalSecs,
        );
    }

    // add initial numbers which will animate to wrapper
    populateInitialValues(wrapperElement, totalVal) {
        let self = this;

        for (let i = totalVal; i >= 0; i--) {
            wrapperElement.innerHTML += '<span class="s' + i + '">' + self.pad(i, 2) + '</span>';
        }
    }

    // slide up animation on number container
    animateValue(container, newVal, totalVal) {
        let self = this;

        container.style.top = self.scrollOffsetEms(newVal, totalVal);
        container.querySelectorAll('span.s' + newVal).forEach(function (el) {
            el.classList.add('active');
        });

        if (newVal == totalVal - 1) {
            container.classList.add('reset-animation');
            setTimeout(function () {
                container.classList.remove('reset-animation');
            }, self.interval / 2);
        }
    }

    switchToLive() {
        // if in spotlight carousel, add is-live class to spotlight carousel container
        let spotlightCarouselContainer = this.findAncestor(
            this.countdownElement,
            '.spotlight-carousel-wrapper',
        );
        if (spotlightCarouselContainer) {
            spotlightCarouselContainer.classList.add('is-live');
        } else {
            // if not in spotlight carousel, add is-live class to countdown element
            this.countdownElement.querySelector('.countdown').classList.add('is-live');
        }
    }

    switchToDone() {
        // add is-done class to spotlight carousel container
        let spotlightCarouselContainer = this.findAncestor(
            this.countdownElement,
            '.spotlight-carousel-wrapper',
        );
        if (spotlightCarouselContainer) {
            spotlightCarouselContainer.classList.add('is-done');
        }
    }

    switchToUpcoming() {
        // add is-upcoming class to spotlight carousel container
        let spotlightCarouselContainer = this.findAncestor(
            this.countdownElement,
            '.spotlight-carousel-wrapper',
        );
        if (spotlightCarouselContainer) {
            spotlightCarouselContainer.classList.add('is-upcoming');
        }
    }

    findAncestor(el, sel) {
        while ((el = el.parentElement) && !(el.matches || el.matchesSelector).call(el, sel));
        return el;
    }

    // return number 00 format
    pad(num, size) {
        num = num.toString();
        while (num.length < size) num = '0' + num;
        return num;
    }

    // calculate top offset
    scrollOffsetEms(val, total) {
        let height = total - val,
            str = '-' + height + 'em';
        return str;
    }
}
